import type {
    ArgumentsSchema,
    AvailableFieldBlockInfo,
    AvailableFilterBlockInfo,
    DataSourceAbstract,
    Field,
    FilterBlockAbstract,
    IndependentAction,
    PageVariableSchema,
    StateSchema,
    TypeInstanceMap,
    VariableADTvalue,
    ViewField,
    ViewType
} from '@lighthouse/core'
import { FilterMode } from '@lighthouse/core'
import React, { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import type { FlowNode, Workflow } from '../../types'
import { getIdVariableData, getRealField } from '../../utils'
import { Fields, MorePopover, Operator, Params } from '../Condition'
import { FieldVariable } from '../Condition/FieldVariable'
import { ParamsVariable } from '../Condition/ParamsVariable'

interface FilterConditionProps {
    children?: React.ReactNode
    prefixName: string
    columns: ViewField[]
    dataSource?: DataSourceAbstract
    availableFieldBlocksInfo?: AvailableFieldBlockInfo[]
    availableFilterBlocksInfo?: AvailableFilterBlockInfo[]
    formDsId?: string
    pageVariables?: PageVariableSchema[]
    pagePathParameter?: ArgumentsSchema[]
    pageQueryParameter?: ArgumentsSchema[]
    pageStateParameter?: StateSchema[]
    upstreamNodes?: FlowNode[]
    allUpstreamNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    dataSourceList: DataSourceAbstract[]
    workflowList?: Workflow[]
    noSettingInnerType?: Set<TypeInstanceMap>
    fieldMode?: FilterMode
    paramsMode?: FilterMode
    enableCurrentUser?: boolean
    viewDsId?: string
    viewType?: ViewType
    extraViewFields?: Field[]
    paramsDataSource?: DataSourceAbstract
    isUseImage?: boolean
    filterBlocks?: FilterBlockAbstract[]
    enableFilterVariable?: boolean
    disabled?: boolean
    disableUserOption?: boolean
    disableDataSourceOption?: boolean
    onDuplicate?: () => void
    onDelete?: () => void
    onMouseEnter?: (blockId: string) => void
    onMouseLeave?: () => void
}

const SCxFlex = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
`

const FilterCondition: React.FC<FilterConditionProps> = ({
    dataSource,
    availableFieldBlocksInfo,
    availableFilterBlocksInfo,
    pagePathParameter,
    pageQueryParameter,
    pageVariables,
    pageStateParameter,
    upstreamNodes,
    allUpstreamNodes,
    allParentActions,
    dataSourceList,
    workflowList,
    prefixName,
    columns,
    noSettingInnerType,
    fieldMode = FilterMode.NORMAL,
    paramsMode = FilterMode.NORMAL,
    enableCurrentUser,
    formDsId,
    viewDsId,
    viewType,
    extraViewFields,
    paramsDataSource,
    isUseImage,
    filterBlocks,
    enableFilterVariable,
    disabled,
    disableUserOption,
    disableDataSourceOption,
    onDuplicate,
    onDelete,
    onMouseEnter,
    onMouseLeave
}) => {
    const { control } = useFormContext()
    const idVariable: VariableADTvalue = useWatch({
        control,
        name: `${prefixName}.idVariable`
    })

    const field = useMemo(() => {
        const viewDataSource = dataSourceList.find(item => item.id === viewDsId)
        return getIdVariableData({
            idVariable,
            dataSourceList,
            sourceFlowNode: upstreamNodes,
            dataSource,
            availableFieldBlocksInfo,
            availableFilterBlocksInfo,
            viewDataSource,
            extraViewFields,
            workflowList,
            pageVariableSchemas: pageVariables,
            pathParameter: pagePathParameter,
            queryParameter: pageQueryParameter,
            stateParameter: pageStateParameter
        })
    }, [
        dataSource,
        dataSourceList,
        availableFieldBlocksInfo,
        availableFilterBlocksInfo,
        idVariable,
        pagePathParameter,
        pageQueryParameter,
        pageStateParameter,
        pageVariables,
        workflowList,
        extraViewFields,
        upstreamNodes,
        viewDsId
    ])
    const realField = useMemo(() => {
        if (!field) {
            return
        }
        const _field = getRealField(field)
        if (!_field) {
            return
        }
        return _field
    }, [field])

    const fieldContent = useMemo(() => {
        if (fieldMode === FilterMode.CUSTOM) {
            return (
                <FieldVariable
                    dataSource={dataSource}
                    pagePathParameter={pagePathParameter}
                    pageQueryParameter={pageQueryParameter}
                    pageStateParameter={pageStateParameter}
                    pageVariables={pageVariables}
                    upstreamNodes={upstreamNodes}
                    allUpstreamNodes={allUpstreamNodes}
                    allParentActions={allParentActions}
                    viewDsId={viewDsId}
                    viewType={viewType}
                    extraViewFields={extraViewFields}
                    isUseImage={isUseImage}
                    workflowList={workflowList}
                    dataSourceList={dataSourceList}
                    availableFieldBlocksInfo={availableFieldBlocksInfo}
                    availableFilterBlocksInfo={availableFilterBlocksInfo}
                    prefixName={prefixName}
                    disabled={disabled}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                />
            )
        }
        return (
            <Fields
                columns={columns}
                dataSource={dataSource}
                noSettingInnerType={noSettingInnerType}
                prefixName={prefixName}
                disabled={disabled}
            />
        )
    }, [
        allUpstreamNodes,
        allParentActions,
        columns,
        dataSource,
        dataSourceList,
        disabled,
        availableFieldBlocksInfo,
        fieldMode,
        availableFilterBlocksInfo,
        noSettingInnerType,
        onMouseEnter,
        onMouseLeave,
        pagePathParameter,
        pageQueryParameter,
        pageStateParameter,
        pageVariables,
        prefixName,
        workflowList,
        upstreamNodes,
        extraViewFields,
        viewDsId,
        viewType,
        isUseImage
    ])

    const paramContent = useMemo(() => {
        if (realField?.type === 'file' || realField?.type === 'video' || realField?.innerType === 'RICH_TEXT') {
            return null
        }
        if (paramsMode === FilterMode.CUSTOM) {
            return (
                <ParamsVariable
                    width="100%"
                    field={realField}
                    dataSource={dataSource}
                    pagePathParameter={pagePathParameter}
                    pageQueryParameter={pageQueryParameter}
                    pageVariables={pageVariables}
                    pageStateParameter={pageStateParameter}
                    upstreamNodes={upstreamNodes}
                    allUpstreamNodes={allUpstreamNodes}
                    allParentActions={allParentActions}
                    viewDsId={viewDsId}
                    viewType={viewType}
                    extraViewFields={extraViewFields}
                    paramsDataSource={paramsDataSource}
                    isUseImage={isUseImage}
                    availableFieldBlocksInfo={availableFieldBlocksInfo}
                    availableFilterBlocksInfo={availableFilterBlocksInfo}
                    dataSourceList={dataSourceList}
                    prefixName={prefixName}
                    formDsId={formDsId}
                    disabled={disabled}
                    disableUserOption={disableUserOption}
                    disableDataSourceOption={disableDataSourceOption}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    filterBlocks={filterBlocks}
                    workflowList={workflowList}
                />
            )
        }
        return <Params width="100%" field={realField} prefixName={prefixName} enableCurrentUser={enableCurrentUser} disabled={disabled} />
    }, [
        realField,
        paramsMode,
        prefixName,
        enableCurrentUser,
        disabled,
        dataSource,
        pagePathParameter,
        pageQueryParameter,
        pageVariables,
        pageStateParameter,
        upstreamNodes,
        allUpstreamNodes,
        allParentActions,
        extraViewFields,
        paramsDataSource,
        viewDsId,
        viewType,
        isUseImage,
        availableFieldBlocksInfo,
        availableFilterBlocksInfo,
        dataSourceList,
        formDsId,
        onMouseEnter,
        onMouseLeave,
        filterBlocks,
        workflowList
    ])

    return (
        <SCxFlex style={{ display: 'flex', flexDirection: 'row' }}>
            <SCxFlex style={{ display: 'flex', flexDirection: 'row' }}>
                {fieldContent}
                <Operator
                    field={field}
                    prefixName={prefixName}
                    clearable={false}
                    disabled={disabled}
                    enableFilterVariable={enableFilterVariable}
                />
                {paramContent}
            </SCxFlex>
            <MorePopover disabled={disabled} onDuplicate={onDuplicate} onDelete={onDelete} />
        </SCxFlex>
    )
}

export default FilterCondition
